import React, { useState } from "react";
import { TextField, Button, Typography, Container, Card, IconButton, InputAdornment, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  KullaniciSifreDegistirRequest,
} from "../../apis/OB/SaveApi/KullaniciSifreDegistir";
import KullaniciSifreDegistir from "../../apis/OB/SaveApi/KullaniciSifreDegistir";
import AuthAPI from "../../apis/AuthAPI";
import { useNavigate } from "react-router-dom";
import SessionStorageService from "../../services/StorageService";
import TcTelefonKullaniciVarMi from "../../components/OB/popups/sifremi-unuttum-popup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
 
interface ChangePasswordProps {
  onSuccess?: () => void;
}
 
const ChangePassword = ({ onSuccess }: ChangePasswordProps) => {
  const navigate = useNavigate();
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState<any>(null);
 
  // Şifre görünürlük state'leri
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
  const userInfo = SessionStorageService.getUserInfo();
 
  const validationSchema = yup.object({
 
    oldPassword: yup.string().required("Eski Şifre zorunludur"),
    newPassword: yup
      .string()
      .min(8, "Yeni Şifre en az 8 karakter olmalıdır")
      .matches(/[A-Z]/, "En az bir büyük harf içermelidir")
      .matches(/[a-z]/, "En az bir küçük harf içermelidir")
      .matches(/[0-9]/, "En az bir rakam içermelidir")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "En az bir noktalama işareti içermelidir"
      )
      .notOneOf([yup.ref("oldPassword")], "Yeni şifre eski şifre ile aynı olamaz!")
      .required("Yeni Şifre zorunludur"),
    newPasswordConfirm: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Şifreler uyuşmuyor !")
      .required("Yeni Şifre Tekrar zorunludur"),
 
  });
 
  const handleSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
    setSubmitting(true);
    try {
      if (!userInfo) {
        toast.error("Kullanıcı bilgileri bulunamadı. Lütfen tekrar giriş yapın.");
        await AuthAPI.signOut();
        navigate("/login");
        setSubmitting(false);
        return;
      }
 
      // eski şifre kontrolü
      const passwordValidation = await AuthAPI.ValidatePassword({
        username: userInfo.vergiNo == null ? userInfo.kullaniciKodu : undefined,
        oldPassword: values.oldPassword,
        vergiNo: userInfo.vergiNo || undefined
      });
 
      if (passwordValidation.sonuc !== 0) {
        toast.error(passwordValidation.sonucAciklama || "Eski şifre yanlış!");
        setSubmitting(false);
        return;
      }
 
      // Eski şifre doğruysa, pending data'yı hazırla
      setPendingPasswordChange({
        userInfo,
        newPassword: values.newPassword,
        resetForm,
      });
 
      setSubmitting(false);
 
      // SMS popup'ı aç
      setTimeout(() => {
        setSmsPopupOpen(true);
      }, 300);
 
    } catch (error: any) {
      console.error("Şifre değiştirme hatası:", error);
      //toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      setSubmitting(false);
    }
  };
 
  const handleSmsSuccess = async () => {
    if (!pendingPasswordChange) return;
 
    try {
      const { userInfo, newPassword, resetForm } = pendingPasswordChange;
     
      // şifreyi güncelle
      const request = new KullaniciSifreDegistirRequest(
        newPassword,
        userInfo.vergiNo == null ? String(userInfo.kullaniciKodu) : undefined,
        userInfo.vergiNo || undefined
      );
 
      const response = await KullaniciSifreDegistir.saveKullaniciSifreDegistirSave(request);
      const result = response.data;
 
      if (result.sonuc === 0) {
        if (onSuccess) {
          onSuccess();
        }
        await AuthAPI.signOut();
        navigate("/login", { state: { passwordChanged: true } });
        resetForm();
        setSmsPopupOpen(false);
        setPendingPasswordChange(null);
      } else {
        toast.error(result.sonucAciklama || "Şifre değiştirme başarısız.");
        setSmsPopupOpen(false);
      }
    } catch (error: any) {
      console.error("Şifre değiştirme hatası:", error);
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      setSmsPopupOpen(false);
    }
  };
 
  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Şifre Değişikliği
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <p>Şifrenizi değiştirebilirsiniz... Yeni şifrenizi aşağıdaki kurallara göre belirlemeniz gerekmektedir:</p>
        <ul>
          <li>Yeni şifreniz en az 8 karakterden oluşmalıdır.</li>
          <li>Yeni şifreniz en az bir küçük harf, en az bir büyük harf, en az bir rakam ve en az bir sembolden (.,:;-_*?!="^+%&/()&lt;&gt;#&#123;&#125;[]'~) oluşmalıdır.</li>
        </ul>
      </Typography>
      <Formik
        initialValues={{
          oldPassword: "",
          newPassword: "",
          newPasswordConfirm: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, values, touched, errors, isSubmitting }) => (
          <Form>
            <TextField
              fullWidth
              margin="normal"
              label="Eski Şifre"
              size="small"
              type={showOldPassword ? "text" : "password"}
              name="oldPassword"
              value={values.oldPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.oldPassword && Boolean(errors.oldPassword)}
              helperText={touched.oldPassword && errors.oldPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Yeni Şifre"
              size="small"
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              value={values.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.newPassword && Boolean(errors.newPassword)}
              helperText={touched.newPassword && errors.newPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Yeni Şifre Tekrar"
              size="small"
              type={showNewPasswordConfirm ? "text" : "password"}
              name="newPasswordConfirm"
              value={values.newPasswordConfirm}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.newPasswordConfirm && Boolean(errors.newPasswordConfirm)
              }
              helperText={
                touched.newPasswordConfirm && errors.newPasswordConfirm
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPasswordConfirm(!showNewPasswordConfirm)}
                      edge="end"
                    >
                      {showNewPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
 
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isSubmitting}
            >
              Şifreyi Değiştir
            </Button>
          </Form>
        )}
      </Formik>
 
 
      {smsPopupOpen && pendingPasswordChange && (
        <TcTelefonKullaniciVarMi
          tcKimlikNo={userInfo.vergi== null ?userInfo.kullaniciKodu: undefined}
          vergiNo={userInfo.vergiNo}
          cepTelefon={userInfo.telefonNo}
          smsControlledSet={(success: boolean) => {
            if (success) {
              handleSmsSuccess();
            }
          }}
          tcKimlikNoSet={(value: any) => {
            // TC Kimlik No set edildiğinde
            console.log("TC Kimlik No:", value);
          }}
          vergiNoSet={(value: any) => {
            // Vergi No set edildiğinde
            console.log("Vergi No:", value);
          }}
        />
      )}
    </Box>
 
  );
};
 
export default ChangePassword;